import { createRoute } from "@tanstack/solid-router";
import { createEffect, on } from "solid-js";

import { rootRoute } from "../__root";
import { useSession } from "@/lib/auth";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { ensureTabAccount } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@listto/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { appUndoStack } from "@/lib/undo";
import { useListGroupsQuery, useListsWithMetaQuery } from "@listto/shared/solid";
import { useActiveDragId } from "@/lib/dnd";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { validateAppLayoutSearch } from "@/features/layout/app-search";
import {
  OFFLINE_CACHE_STALE_TIME_MS,
  SESSION_CHECK_TIMEOUT_MS,
  SESSION_STALE_TIME_MS,
} from "@/lib/constants";

export { useAppLayoutContext } from "@/features/layout/context";

function AppLayout() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  // L1 (undo): the undo stack is per active account. Switching accounts
  // clears it so a Ctrl+Z does not apply mutations from another account.
  createEffect(on(userId, () => appUndoStack.clear(), { defer: true }));

  const activeDragId = useActiveDragId();

  const lists = useListsWithMetaQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const listGroups = useListGroupsQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  return (
    <AppLayoutContent
      db={db()}
      activeListId={
        activeDragId()?.startsWith("list-") ? (activeDragId()?.replace("list-", "") ?? null) : null
      }
      activeDragId={activeDragId}
      lists={lists}
      listGroups={listGroups}
    />
  );
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: if the user explicitly chose the GUEST account, do NOT verify
  // with the server. The backend cookie may still authenticate a real account,
  // and pushing that into the machine here would flip the app back to that real
  // identity (and swap the active PowerSync DB away from the guest). Honor the
  // explicit guest choice; the real session is re-primed on switching back.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  // The redirect is thrown outside try/catch so TanStack Router receives it.
  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      // Add a timeout to avoid hanging the route transition if the server is slow/rate-limited.
      // P23/CC-60 · I1-bis: value comes from `lib/constants.ts`. It was a local
      // literal, which is why `_auth/route.tsx` —same call, same risk— never
      // inherited this cap and left `/login` blank indefinitely.
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout while verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    // Network error / timeout — DO NOT send VERIFY_COMPLETE. Marking the
    // machine as verified would lock the app in visitor mode permanently,
    // because every subsequent route load would early-return on `isVerified`
    // and the in-component `useSession` query is enabled by `!isPriming()`
    // alone. Leaving `isVerified: false` lets the next navigation retry
    // and `useSession` retry on mount.
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      // Server explicitly rejected auth — allow access as a visitor/guest.
      // (SERVER_REPLY above already handled this, but we return early)
      return;
    }
    // Other server error — allow offline access.
    // Ensure we are marked as verified if the above SERVER_REPLY was skipped or didn't set it.
    // (Actually SERVER_REPLY always sets isVerified: true in our machine)
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Pathless layout route: the app tree hangs off `/` directly. Which ACCOUNT a
  // tab shows is per-tab state in `sessionStorage` (see `lib/tab-account.ts`),
  // NOT a URL segment — so every in-app URL names only a RESOURCE and stays
  // portable across devices and accounts. See `infra/MULTIACCOUNT_URL_OPTIONS.md`.
  id: "_app",
  // Global search-state for overlays mounted at `_app` level (command palette +
  // global dialogs). Gives each overlay its own copyable URL. Child route
  // search params (e.g. `/?q=`) merge with these.
  validateSearch: validateAppLayoutSearch,
  beforeLoad: () => {
    // Make THIS tab's account pin explicit on entering the app tree, so every
    // later synchronous resolution (the `X-Active-Account` header, the session
    // machine, the PowerSync DB binding) reads a stable value instead of
    // re-deriving the device-wide default — which another tab can move.
    // An empty registry means a cold first visit: mint the guest (idempotent),
    // and the pin lands on the next pass.
    if (ensureTabAccount(readAccountsStateSync()) === null) {
      void accountsStore.ensureGuestActive();
    }

    const snapshot = sessionActor.getSnapshot();

    // Hot path: session already established and machine is done priming.
    // We only prefetch if the cached data is stale — useSession's createQuery
    // will handle refetching reactively once mounted.
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    // Cold path: fire the verify in the background and let the route render
    // immediately. The InitialSyncOverlay covers the UI while the machine is
    // priming/verifying, and the AppLayout component reactively redirects to
    // /login if the verify resolves to an unauthenticated user. This matches
    // the behaviour of `apps/native`: routing is never gated on a network
    // round-trip, and the user never sees a frozen login form.
    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
    // Intentional fire-and-forget — see comment above.
  },

  component: AppLayout,
});
